import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, Clock, CheckCircle, XCircle, RefreshCw, TrendingUp, AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const CHART_MARGIN = { top: 4, right: 8, left: -16, bottom: 0 };
const AXIS_TICK = { fontSize: 12, fill: "#94a3b8" };
const TOOLTIP_CURSOR = { fill: "#f8fafc" };
const BAR_RADIUS = [4, 4, 0, 0];

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    confirmed: "bg-green-100 text-green-800 border-green-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 py-0.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-600 capitalize">{entry.name}:</span>
          <span className="font-semibold text-slate-800">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [bouncedEmails, setBouncedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.getAdminBookings().then((r) => setBookings(r.data.bookings)),
      api.getAdminStats().then((r) => setStats(r.data)),
      api.getSuppressedEmails().then((r) => setBouncedEmails(r.data.suppressed_emails || [])).catch(() => { /* suppression list is supplementary */ }),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleRemoveBounced = async (email) => {
    try {
      await api.removeSuppressedEmail(email);
      setBouncedEmails((prev) => prev.filter((e) => e.email !== email));
    } catch (e) { console.debug("dismiss bounced email failed:", e?.message); }
  };

  useEffect(() => { fetchData(); }, []);

  const recent = [...bookings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  const summary = stats?.summary || { total: 0, pending: 0, confirmed: 0, cancelled: 0 };
  const thisWeek = stats?.this_week ?? 0;
  const monthly = stats?.monthly || [];

  // Show the next 6 months (first 6 from the forward-looking array)
  const chartData = monthly.slice(0, 6);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Dashboard</h1>
          <p className="text-slate-500 mt-1">Overview of all accessible bike bookings</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2 border-green-200 text-green-700 hover:bg-green-50" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={CalendarDays} label="Total Bookings" value={summary.total} color="blue" testId="stat-total" />
        <StatCard icon={CalendarDays} label="This Week" value={thisWeek} color="teal" testId="stat-this-week" />
        <StatCard icon={Clock} label="Pending" value={summary.pending} color="amber" testId="stat-pending" onClick={() => navigate("/admin/bookings")} />
        <StatCard icon={CheckCircle} label="Confirmed" value={summary.confirmed} color="green" testId="stat-confirmed" onClick={() => navigate("/admin/bookings")} />
        <StatCard icon={XCircle} label="Cancelled" value={summary.cancelled} color="red" testId="stat-cancelled" />
      </div>

      {/* Bounced Emails Alert */}
      {bouncedEmails.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="bounced-emails-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">Bounced / Complained Emails ({bouncedEmails.length})</h3>
          </div>
          <p className="text-amber-700 text-sm mb-3">These emails failed to deliver. Check the requester's email in their booking, correct it if needed, and resend the confirmation.</p>
          <div className="space-y-2">
            {bouncedEmails.map((item) => (
              <div key={item.email} className="flex items-center justify-between bg-white rounded-lg border border-amber-100 px-4 py-2.5" data-testid={`bounced-email-${item.email}`}>
                <div>
                  <span className="font-medium text-slate-800">{item.email}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    item.reason === "bounce" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"
                  }`}>
                    {item.reason === "bounce" ? `Bounce (${item.bounce_type || "unknown"})` : "Complaint"}
                  </span>
                  {item.diagnostic && (
                    <p className="text-slate-400 text-xs mt-0.5 truncate max-w-md">{item.diagnostic}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                  onClick={() => handleRemoveBounced(item.email)}
                  title="Dismiss — remove from suppression list"
                  data-testid={`remove-bounced-${item.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly trend chart */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mb-8" data-testid="monthly-chart">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-green-700" />
          <div>
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Monthly Trends</h2>
            <p className="text-slate-500 text-sm">Bookings by status over the next 6 months</p>
          </div>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="h-56 flex items-center justify-center text-slate-400">Loading chart...</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={CHART_MARGIN} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => <span style={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>{v}</span>}
                />
                <Bar dataKey="confirmed" fill="#22c55e" radius={BAR_RADIUS} name="confirmed" />
                <Bar dataKey="pending" fill="#f59e0b" radius={BAR_RADIUS} name="pending" />
                <Bar dataKey="cancelled" fill="#f87171" radius={BAR_RADIUS} name="cancelled" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Full monthly breakdown table */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mb-8" data-testid="monthly-table">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Month-by-Month Breakdown</h2>
          <p className="text-slate-500 text-sm">All booking activity over the next 12 months</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Month</th>
                <th className="text-center px-4 py-3 font-semibold">Total</th>
                <th className="text-center px-4 py-3 font-semibold text-green-700">Confirmed</th>
                <th className="text-center px-4 py-3 font-semibold text-amber-600">Pending</th>
                <th className="text-center px-4 py-3 font-semibold text-red-500">Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m, i) => (
                <tr key={m.year_month} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} data-testid={`monthly-row-${m.year_month}`}>
                  <td className="px-6 py-3 font-medium text-slate-700">{m.label}</td>
                  <td className="px-4 py-3 text-center font-bold text-slate-800">{m.total}</td>
                  <td className="px-4 py-3 text-center">
                    {m.confirmed > 0 ? <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-full font-semibold text-xs">{m.confirmed}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {m.pending > 0 ? <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full font-semibold text-xs">{m.pending}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {m.cancelled > 0 ? <span className="px-2.5 py-1 bg-red-50 text-red-600 rounded-full font-semibold text-xs">{m.cancelled}</span> : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent bookings */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="recent-bookings">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Recent Bookings</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/bookings")} className="text-green-700 text-sm hover:bg-green-50">
            View all
          </Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : recent.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No bookings yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Requester</th>
                <th className="text-left px-6 py-3 font-semibold">Date & Time</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
                <th className="text-left px-6 py-3 font-semibold">Volunteer</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((b, i) => (
                <tr key={b.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-6 py-3 font-medium text-slate-800">{b.requester_name}</td>
                  <td className="px-6 py-3 text-slate-600">{b.booking_dt_formatted}</td>
                  <td className="px-6 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-6 py-3 text-slate-500">{b.claimed_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId, onClick }) {
  const colors = {
    blue:  { bg: "bg-blue-50",  text: "text-blue-700",  border: "border-blue-100"  },
    teal:  { bg: "bg-teal-50",  text: "text-teal-700",  border: "border-teal-100"  },
    amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-100" },
    green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-100" },
    red:   { bg: "bg-red-50",   text: "text-red-600",   border: "border-red-100"   },
  };
  const c = colors[color] || colors.blue;
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`bg-white rounded-2xl border border-green-100 shadow-sm p-5 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${c.bg} ${c.border} border`}>
        <Icon className={`w-4 h-4 ${c.text}`} />
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-slate-500 text-xs mt-0.5">{label}</p>
    </div>
  );
}
